import * as React from "react";
import { styled, useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import DataConfig from "./_config.json";

import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import CssBaseline from "@mui/material/CssBaseline";
import MuiAppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import SwipeableDrawer from "@mui/material/SwipeableDrawer";
// Lazy-loaded page components for code splitting
const Inventory = React.lazy(() => import("./PSInventory/Inventory"));
const CSSavecustomer = React.lazy(() => import("./PSSavecustomer/Savecustomer"));

// Report components
const PDIReport = React.lazy(() => import("./PSReport/PSPDIReport/mainreport"));
const YamahaReport = React.lazy(() => import("./PSReport/PSYamahaReport/mainReport"));
const Mainreport = React.lazy(() => import("./PSReport/mainreport"));

// Purchase & Order components
const PSORDERPR = React.lazy(() => import("./PSORDERPR/Main"));
const PSPURCHASE = React.lazy(() => import("./PSPURCHASE/Main"));
const PSPaybill = React.lazy(() => import("./PSPaybill/Main"));
const PSOrder = React.lazy(() => import("./PSOrder/Main"));
const PSOrderBook = React.lazy(() => import("./PSOrderBook/index"));

// Sales components
const PSCCSaleOrder = React.lazy(() => import("./PSCC_SaleOrder/Main"));
const PSquot = React.lazy(() => import("./PSquot/Main.js"));
const PSSaleAndService = React.lazy(() => import("./PSSaleAndService/Main"));
const PSSalesOrder = React.lazy(() => import("./PSSalesOrder/Main"));
const PSReturnProduct = React.lazy(() => import("./PSReturnProduct/Main"));
const PSSaleRet = React.lazy(() => import("./PSSaleRet/Main"));

// Banking & Finance components
const PSCsbankcashin = React.lazy(() => import("./PSCsbankcashin/Main"));
const PSApotherdebt = React.lazy(() => import("./PSApotherdebt/Main"));
const PSPAYMENT = React.lazy(() => import("./PSPAYMENT/Main"));
const PSARDeposit = React.lazy(() => import("./PSARDeposit/Main"));
const PSARCNDeposit = React.lazy(() => import("./PSARCNDeposit/Main"));
const PSReceipt = React.lazy(() => import("./PSReceipt/Main"));

// Transfer & Warehouse components
const PSSaveAskTransfer = React.lazy(() => import("./PSTransferProduct/PSSaveAskTransfer/Main"));
const PSSaveTransfer = React.lazy(() => import("./PSTransferProduct/PSSaveTransfer/Main"));

// Expenses & Income components
const PSExpenses = React.lazy(() => import("./PSExpensesAndBad/PSExpenses/Main"));
const PSOtherBad = React.lazy(() => import("./PSExpensesAndBad/PSOtherBad/Main"));

// Other components
const Savecreditor = React.lazy(() => import("./PSSavecreditor/Savecreditor"));
const PSMPSummit = React.lazy(() => import("./PSMPSummit/PSMPSummit"));
const CSWithdrawalrequest = React.lazy(() => import("./PSWithdrawalrequest/Withdrawalrequest"));
const CSProductbring = React.lazy(() => import("./PSProductbring/Productbring"));

// Note: PSToHonda, PSFG, PSSTKJOBORDER are exported from index - need to check if they can be lazy loaded
import { PSToHonda, PSFG, PSSTKJOBORDER } from "./Pages/index";
import styles from "./assets/jss/psstyle/component/headerStyle";

// Keep core pages as normal imports (not lazy) for instant access
import Login from "./login";
import Home from "./home";

// Admin page can also be lazy-loaded
const Admin = React.lazy(() => import("./View/admin/main/index"));

import { Routes, Route, Link, Navigate, useNavigate } from "react-router-dom";
import Hidden from "@mui/material/Hidden";
import { drawerWidth, appbarHeight } from "./assets/jss/psstyle/psacount";
import AccountMenu from "./Components/Navbars/Acountmenu";
import parseJwt from "./CustomFunction/parseJWT.js";
import Logout from "./CustomFunction/User/logout";
import { genpostURLTEST, genURL, genURLWithAuth, getDataWithAuth } from "./_getjson";
import axios from "axios";
import RichObjectTreeView from "./Components/Slidebar/RichObjectTreeView";
import HourglassEmptyRoundedIcon from "@mui/icons-material/HourglassEmptyRounded";
import ChangelogModalWrapper from "./Components/ChangelogModal/ChangelogModalWrapper";

// Countdown Timer Component
const CountdownTimer = () => {
  const navigate = useNavigate();
  const [timeOutText, setTimeOutText] = React.useState("");

  React.useEffect(() => {
    if (localStorage.getItem("Token")) {
      const TimeOut = parseJwt(localStorage.getItem("Token")).exp * 1000;
      
      const interval = setInterval(function () {
        const now = new Date().getTime();
        const distance = TimeOut - now;

        const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((distance % (1000 * 60)) / 1000);

        setTimeOutText(hours + "h " + minutes + "m " + seconds + "s ");
        
        if (distance < 0) {
          clearInterval(interval);
          navigate("/logout");
        }
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [navigate]);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.5,
        fontSize: "12px",
        fontWeight: 500,
      }}
    >
      <HourglassEmptyRoundedIcon sx={{ fontSize: 16 }} />
      <span>{timeOutText}</span>
    </Box>
  );
};

// Version Display Component
const VersionDisplay = ({ onVersionClick }) => {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.5,
        fontSize: "12px",
        fontWeight: 500,
        cursor: "pointer",
      }}
      onClick={onVersionClick}
    >
      <span>{process.env.REACT_APP_MAINURL} V.{process.env.REACT_APP_VERSION}</span>
    </Box>
  );
};

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  height: appbarHeight,
  ...styles.appBar,
  display: "flex",
  transition: theme.transitions.create(['margin', 'width'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    [theme.breakpoints.up('md')]: {
      width: `calc(100% - ${drawerWidth}px)`,
      marginLeft: `${drawerWidth}px`,
      transition: theme.transitions.create(['margin', 'width'], {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.enteringScreen,
      }),
    },
  }),
}));

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
  justifyContent: "flex-end",
  height: appbarHeight,
  ...styles.DrawerHeader,
}));

// Auth checks and permission loading moved to useEffect inside component

// Loading fallback component for lazy-loaded routes
const PageLoader = () => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      backgroundColor: '#f5f5f5'
    }}
  >
    <div style={{ textAlign: 'center' }}>
      <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
        <span className="sr-only">Loading...</span>
      </div>
      <p style={{ marginTop: '1rem', color: '#666' }}>กำลังโหลด...</p>
    </div>
  </Box>
);

// RouterPath must contain only <Route> elements (no Suspense wrapper here)
let RouterPath = (
  <>
    <Route index path="/home" element={<Home />} />
    <Route path="/login" element={<Login />} />
    <Route path="/Inventory" element={<Inventory />} />
    <Route path="/Savecreditor" element={<Savecreditor />} />
    <Route path="/Savecustomer" element={<CSSavecustomer />} />
    <Route path="/PSORDERPR" element={<PSORDERPR />} />
    <Route path="/PSPURCHASE" element={<PSPURCHASE />} />
    <Route path="/PSARDeposit" element={<PSARDeposit />} />
    <Route path="/PSARCNDeposit" element={<PSARCNDeposit />} />
    <Route path="/PSReceipt" element={<PSReceipt />} />
    <Route path="/PSPaybill" element={<PSPaybill />} />
    <Route path="/PSCsbankcashin" element={<PSCsbankcashin />} />
    <Route path="/PSCC_SaleOrder" element={<PSCCSaleOrder />} />
    <Route path="/PSOrder" element={<PSOrder />} />
    <Route path="/PSMPSummit" element={<PSMPSummit />} />
    <Route path="/PSquot" element={<PSquot />} />
    <Route path="/PSSaleAndService" element={<PSSaleAndService />} />
    <Route path="/PSSalesOrder" element={<PSSalesOrder />} />
    <Route path="/PSReturnProduct" element={<PSReturnProduct />} />
    <Route path="/PSSaleRet" element={<PSSaleRet />} />
    <Route path="/PSApotherdebt" element={<PSApotherdebt />} />
    <Route path="/PSPAYMENT" element={<PSPAYMENT />} />
    <Route path="/PSSaveAskTransfer" element={<PSSaveAskTransfer />} />
    <Route path="/PSSaveTransfer" element={<PSSaveTransfer />} />
    <Route path="/PSExpenses" element={<PSExpenses />} />
    <Route path="/PSOtherBad" element={<PSOtherBad />} />
    <Route path="/PSOrderBook" element={<PSOrderBook />} />
    <Route path="/CSWithdrawalrequest" element={<CSWithdrawalrequest />} />
    <Route path="/CSProductbring" element={<CSProductbring />} />
    <Route path="/PDIReport" element={<PDIReport />} />
    <Route path="/YamahaReport" element={<YamahaReport />} />
    <Route path="/manage" element={<Admin />} />
    <Route path="/report/:id" element={<Mainreport />} />
    <Route path="/PSToHonda" element={<PSToHonda />} />
    <Route path="/PSFG" element={<PSFG />} />
    <Route path="/PSSTKJOBORDER" element={<PSSTKJOBORDER />} />
    <Route path="*" element={<NoMatch />} />
  </>
);

function NoMatch() {
  const navigate = useNavigate();

  React.useEffect(() => {
    const token = localStorage.getItem("Token");
    
    if (token) {
      try {
        const datenow = Math.round(new Date().getTime() / 1000);
        const dateExp = parseJwt(token).exp;
        
        if (datenow > dateExp) {
          Logout();
          navigate('/login', { replace: true });
        } else {
          navigate('/home', { replace: true });
        }
      } catch (error) {
        Logout();
        navigate('/login', { replace: true });
      }
    } else {
      Logout();
      navigate('/login', { replace: true });
    }
  }, [navigate]);

  return null;
}

export default function PersistentDrawerLeft() {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [open, setOpen] = React.useState(false);
  const [openToggle, setOpenToggle] = React.useState(false);
  const [categoryReport, setCategoryReport] = React.useState([]);
  const [menuData, setMenuData] = React.useState([]);
  const [isInitialized, setIsInitialized] = React.useState(false);
  const [showChangelog, setShowChangelog] = React.useState(false);

  const handleVersionClick = () => {
    setShowChangelog(true);
  };

  const handleCloseChangelog = () => {
    setShowChangelog(false);
  };

  // Auth and route protection
  React.useEffect(() => {
    const token = localStorage.getItem("Token");
    const pathname = window.location.pathname;

    // ตรวจสอบว่า token หมดอายุหรือไม่
    if (token) {
      try {
        const parsed = parseJwt(token);
        const datenow = Math.round(new Date().getTime() / 1000);
        const dateExp = parsed.exp;
        
        if (datenow > dateExp) {
          console.warn('⏰ Token expired - clearing and redirecting to login');
          localStorage.removeItem('Token');
          navigate('/login', { replace: true });
          return;
        }
      } catch (error) {
        console.error('❌ Error parsing token:', error);
        localStorage.removeItem('Token');
        navigate('/login', { replace: true });
        return;
      }
    }

    if (token && pathname === "/login") {
      navigate('/home', { replace: true });
    } else if (!token && pathname !== "/login") {
      navigate('/login', { replace: true });
    } else if (token && pathname === "/logout") {
      Logout();
      navigate('/login', { replace: true });
    }
  }, [navigate]);

  // Permission check
  React.useEffect(() => {
    const fetchPermissions = async () => {
      const token = localStorage.getItem("Token");
      const username = localStorage.getItem("Username");
      
      if (!token || !username) return;

      try {
        const url = `CSRIGHT/Checkpermision?usercode=${username}`;
        const response = await axios.get(genURL(url), { timeout: 3600 });
        
        if (response.data && Array.isArray(response.data) && response.data.length > 0) {
          const permissionData = response.data[0];
          localStorage.setItem("checkoftxt", JSON.stringify(permissionData));
          localStorage.setItem("Editamountpermision", permissionData.AMOUNT || "");
          localStorage.setItem("Permissiongroup", permissionData.CODE || "");
        } else {
          localStorage.setItem("checkoftxt", JSON.stringify({}));
          localStorage.setItem("Editamountpermision", "");
          localStorage.setItem("Permissiongroup", "");
        }
      } catch (error) {
        localStorage.setItem("checkoftxt", JSON.stringify({}));
        localStorage.setItem("Editamountpermision", "");
        localStorage.setItem("Permissiongroup", "");
      }
    };

    fetchPermissions();
  }, []);

  // Initial setup
  React.useEffect(() => {
    fetchMenu();
    fetchCategory();
    // eslint-disable-next-line
  }, []);

  // Initialize drawer state based on screen size
  React.useEffect(() => {
    if (localStorage.getItem("Token")) {
      checkOpenToggle();
    }
    // eslint-disable-next-line
  }, []);

  // Force close drawer on mobile when screen size changes or on initial load
  // ปิด useEffect นี้เพื่อให้จำค่าได้ทุกอุปกรณ์
  // React.useEffect(() => {
  //   if (isMobile && open) {
  //     setOpen(false);
  //     localStorage.setItem("OpenToggle", "false");
  //   }
  // }, [isMobile]);

  const checkOpenToggle = () => {
    const savedState = localStorage.getItem("OpenToggle");
    
    // อ่านค่าจาก localStorage ทุกอุปกรณ์
    if (savedState === null || savedState === undefined) {
      // ค่าเริ่มต้น: Desktop เปิด, Mobile ปิด
      if (isMobile) {
        setOpen(false);
        localStorage.setItem("OpenToggle", "false");
      } else {
        setOpen(true);
        localStorage.setItem("OpenToggle", "true");
      }
    } else if (savedState === "false") {
      setOpen(false);
    } else {
      setOpen(true);
    }
    
    setIsInitialized(true);
  };

  const toggleDrawer = (open) => (event) => {
    setOpen(open);
  };
  const handleDrawerOpen = () => {
    setOpen(true);
    localStorage.setItem("OpenToggle", "true");
  };
  const handleDrawerClose = () => {
    setOpen(false);
    localStorage.setItem("OpenToggle", "false");
  };
  const handleDrawerToggle = () => {
    setOpenToggle((prev) => !prev);
  };

  const handleMenuClick = () => {
    // ปิด drawer เมื่อคลิกเมนู (เฉพาะ Mobile)
    if (isMobile) {
      handleDrawerClose();
    }
  };

  const handleToolbarMenuClick = () => {
    // ปิด SwipeableDrawer (Toolbar) เมื่อคลิกเมนู
    setOpenToggle(false);
  };

  const fetchMenu = async () => {
    try {
      const username = localStorage.getItem("Username");
      if (!username) return;
      
      const url = `CSRIGHT/GetMenu?user=${username}`;
      const response = await axios.get(genURL(url));
      
      // Ensure response and response.data exist
      if (response && response.data) {
        // If it's an array, use it directly; otherwise wrap in array or use empty array
        if (Array.isArray(response.data)) {
          setMenuData(response.data);
        } else {
          console.warn('fetchMenu: response.data is not an array, setting empty array', response.data);
          setMenuData([]);
        }
      } else {
        setMenuData([]);
      }
    } catch (error) {
      console.error('fetchMenu error:', error);
      setMenuData([]);
    }
  };

  const fetchCategory = async () => {
    try {
      const url = "reportmanage/category?filter=active";
      const fullURL = genURLWithAuth(url);
      const response = await getDataWithAuth(fullURL);

      let elementCategory = [];
      
      // Ensure response and response.data exist and is an array
      if (response && response !== "error" && response.data && Array.isArray(response.data)) {
        elementCategory = response.data.filter(
          (item) => item && item.catelevel === 0
        );
        setCategoryReport(elementCategory);
      } else {
        // Data is not in expected format
        console.warn('fetchCategory: response.data is not an array', response?.data);
        setCategoryReport([]);
      }
    } catch (error) {
      console.error('fetchCategory error:', error);
      console.error('fetchCategory error message:', error.message);
      console.error('fetchCategory error response:', error.response);
      setCategoryReport([]);
    }
  };
  return (
    <>
      <Box sx={{ display: "flex", flexGrow: 1 }}>
        <CssBaseline />
        <AppBar position="static" open={open}>
          <Toolbar>
            {localStorage.getItem("Token") !== null ? (
              <>
                <Box component="div" sx={{ flexGrow: 1 }}>
                  <IconButton
                    color="inherit"
                    aria-label="open drawer"
                    onClick={handleDrawerOpen}
                    edge="start"
                    sx={{ mr: 2, ...(open && { display: "none" }) }}
                  >
                    <MenuIcon />
                  </IconButton>
                </Box>
                <Box>
                  <Hidden mdDown implementation="css">
                    <AccountMenu categoryReport={categoryReport} onMenuClick={handleToolbarMenuClick} />
                  </Hidden>
                  <Hidden mdUp implementation="css">
                    <IconButton
                      color="inherit"
                      aria-label="open drawer"
                      onClick={handleDrawerToggle}
                    >
                      <MenuIcon />
                    </IconButton>
                  </Hidden>
                </Box>
              </>
            ) : null}
          </Toolbar>
        </AppBar>
        {localStorage.getItem("Token") !== null ? (
          <Drawer
            sx={{
              width: open ? drawerWidth : 0,
              flexShrink: 0,
              transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.leavingScreen,
              }),
              ...(open && {
                transition: theme.transitions.create('width', {
                  easing: theme.transitions.easing.easeOut,
                  duration: theme.transitions.duration.enteringScreen,
                }),
              }),
              "& .MuiDrawer-paper": {
                width: drawerWidth,
                boxSizing: "border-box",
                overflowY: "hidden",
                overflowX: "hidden",
                display: "flex",
                flexDirection: "column",
              },
            }}
            variant={isMobile ? "temporary" : "persistent"}
            anchor="left"
            open={open}
            onClose={isMobile ? handleDrawerClose : undefined}
            ModalProps={isMobile ? {
              keepMounted: true, // Better open performance on mobile.
            } : undefined}
          >
            <DrawerHeader>
              <IconButton onClick={handleDrawerClose}>
                {theme.direction === "ltr" ? (
                  <ChevronLeftIcon />
                ) : (
                  <ChevronRightIcon />
                )}
              </IconButton>
            </DrawerHeader>
            <Divider />
            {/* มีผลต่อความเร็ว */}
            {/* <Slidebar /> */}
            <Box sx={{ flexGrow: 1, overflowY: "auto", overflowX: "hidden" }}>
              <RichObjectTreeView 
                data={Array.isArray(menuData) ? menuData : []} 
                onMenuClick={handleMenuClick}
              />
            </Box>
            <Divider />
            {/* Footer ด้านล่าง - แบ่งครึ่งซ้าย-ขวา */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                padding: "8px 12px",
                backgroundColor: "#f5f5f5",
                borderTop: "1px solid #e0e0e0",
              }}
            >
              <Box sx={{ flex: 1, display: "flex", justifyContent: "flex-start" }}>
                <CountdownTimer />
              </Box>
              <Box sx={{ flex: 1, display: "flex", justifyContent: "flex-end" }}>
                <VersionDisplay onVersionClick={handleVersionClick} />
              </Box>
            </Box>
          </Drawer>
        ) : null}
        {/* end Drawer left */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            padding: 0,
            height: "100vh",
            paddingTop: appbarHeight,
            overflowY: "auto",
            overflowX: "hidden",
            position: "relative",
            display: "flex",
            flexDirection: "column",
            marginLeft: 0,
            width: '100%',
          }}
        >
          <Box sx={{ flexGrow: 1, overflowY: "auto", overflowX: "hidden" }}>
            <React.Suspense fallback={<PageLoader />}>
              <Routes>{RouterPath}</Routes>
            </React.Suspense>
          </Box>
        </Box>
      </Box>
      <div>
        <React.Fragment key={"top"}>
          <SwipeableDrawer
            anchor={"top"}
            open={openToggle}
            onClose={toggleDrawer(false)}
            onOpen={toggleDrawer(true)}
          >
            <Box sx={{ ...styles.SwipeableDrawer }}>
              <AccountMenu categoryReport={categoryReport} onMenuClick={handleToolbarMenuClick} />
            </Box>
          </SwipeableDrawer>
        </React.Fragment>
      </div>
      {/* Changelog Modal */}
      <ChangelogModalWrapper
        open={showChangelog}
        onClose={handleCloseChangelog}
      />
    </>
  );
}
